/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { generateSimplestList, SimpleAttributeList, simpleLocalAttribute } from '@touchplan/common';
import { DefaultSplitDataRenderType, SplitData } from '@touchplan/common';
import { FileViewState } from '../../modules/file-attach/attachments-view-state';
import { getFileType } from '../../utils/file-utils';
import { IProjectFile } from './ProjectFile';

export interface IAttachment {
	createdAt: string;
	createdBy: string;
	mimeType: string;
	filename: string;
	fileId: string;

	// image attachments
	type?: string; // file type
	description?: string; // a.k.a. caption
	previewFileId?: string;
	thumbnailFileId?: string;
	file?: IProjectFile; // e.g. fullImage
	previewFile?: IProjectFile;
	thumbnailFile?: IProjectFile;
	// below are gcs blob urls for local use
	fileUrl?: string; // e.g. fullImageUrl
	previewFileUrl?: string;
	thumbnailFileUrl?: string;

	// for ui
	viewState?: FileViewState;
	thumbnailFetched?: boolean;
	previewFetched?: boolean;
}

/*
 * e.g. ticketAttachmentId: ticketAttachment
 */
export interface ITicketAttachments {
	[key: string]: IAttachment;
}

/*
 * e.g.
 * ticketId: {
 *	ticketAttachmentId: ticketAttachment
 * }
 */
export interface ITicketAttachmentList {
	[key: string]: ITicketAttachments;
}

export class Attachment extends SplitData<IAttachment, DefaultSplitDataRenderType> {
	protected useInPlace = true;
	protected attributeList: SimpleAttributeList = Object.assign(
		generateSimplestList([
			'createdAt',
			'createdBy',
			'mimeType',
			'filename',
			'description',
			'fileId',
			'previewFileId',
			'thumbnailFileId',
		]),
		{
			type: simpleLocalAttribute,
			file: simpleLocalAttribute,
			previewFile: simpleLocalAttribute,
			thumbnailFile: simpleLocalAttribute,
			fileUrl: simpleLocalAttribute,
			previewFileUrl: simpleLocalAttribute,
			thumbnailFileUrl: simpleLocalAttribute,
			viewState: simpleLocalAttribute,
			thumbnailFetched: simpleLocalAttribute,
			previewFetched: simpleLocalAttribute,
		}
	);

	constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}

	public databaseUpdate(data: IAttachment): void {
		super.databaseUpdate(data);
		this.database.type ||= getFileType(data.mimeType);
		this.database.viewState ||= FileViewState.Default;
	}

	public localDatabaseUpdate(data: IAttachment): void {
		super.databaseUpdate(data);
		this.database.type ||= getFileType(data.mimeType);
		this.database.viewState ||= FileViewState.Loading;
		this.database.thumbnailFetched = true;
		this.database.previewFetched = true;
	}
}
